import { db } from "~db/db";
import { apiBackOffice, apiCadastro, apiSimulacao, apiMapaMina, apiTabelaPreco, apiFunilObra, apiRoteirizacao } from "~/services/utils/api";

const MIN_CONNECTION_SPEED = 0.1;
const OFFLINE_RESPONSE = { data: { isOffline: true } };

const apiMap = {
  backOffice: apiBackOffice,
  cadastro: apiCadastro,
  simulacao: apiSimulacao,
  mapaMina: apiMapaMina,
  tabelaPreco: apiTabelaPreco,
  funilObra: apiFunilObra,
  roteirizacao: apiRoteirizacao,
};

export default class apiGenericClient {
  constructor(apiName, endpoint) {
    this.apiName = apiName;
    this.api = apiMap[apiName];
    this.endpoint = endpoint;
  }

  _validateInitialization() {
    if (!this.api || !this.endpoint) {
      throw new Error(`API "${this.apiName}" ou rota "${this.endpoint}" não encontrada.`);
    }
  }

  async _isOnlineWithGoodConnection() {
    if (!navigator.onLine) return false;
    
    const connection = await this.getNetworkConnection();
    return connection?.downlink > MIN_CONNECTION_SPEED;
  }

  // Método unificado para lidar com offline
  async _handleOfflineRequest(method, payload = null) {
    await this.savePendingRequest(method, this.apiName, this.endpoint, payload);
    return OFFLINE_RESPONSE;
  }

  async getData(params = {}) {
    this._validateInitialization();

    try {
      const isOnlineWithGoodConnection = await this._isOnlineWithGoodConnection();
      
      if (!isOnlineWithGoodConnection) {
        return null;
      }

      const response = await this.api.get(this.endpoint, params);

      // Considera apenas status 500 como erro de servidor
      if (response?.status === 500) {
        throw new Error(`Erro interno do servidor para ${this.endpoint}`);
      }

      return response;
    } catch (err) {
      console.error(`Erro em getData para ${this.endpoint}:`, err);
      throw err;
    }
  }

  async createData(payload) {
    this._validateInitialization();

    // Validação básica do payload
    if (!payload || (typeof payload === 'object' && Object.keys(payload).length === 0)) {
      throw new Error('Payload é obrigatório para createData');
    }

    try {
      const isOnlineWithGoodConnection = await this._isOnlineWithGoodConnection();
      
      if (!isOnlineWithGoodConnection) {
        return await this._handleOfflineRequest('POST', payload);
      }

      const response = await this.api.post(this.endpoint, payload);

      // Se status 500, salva para tentar offline
      if (response?.status === 500) {
        return await this._handleOfflineRequest('POST', payload);
      }

      return response;
    } catch (err) {
      console.error(`Erro em createData para ${this.endpoint}:`, err);
      
      // Em caso de erro de rede, tenta salvar offline
      if (err.code === 'NETWORK_ERROR' || err.message.includes('Network')) {
        return await this._handleOfflineRequest('POST', payload);
      }
      
      throw err;
    }
  }

  async savePendingRequest(method, apiName, endpoint, data) {
    try {
      await db.pendingRequests.add({ 
        method, 
        apiName, 
        endpoint, 
        data,
        timestamp: Date.now(),
        retryCount: 0
      });
    } catch (error) {
      console.error('Erro ao salvar request pendente:', error);
    }
  }

  getNetworkConnection() {
    return (
      navigator.connection ||
      navigator.mozConnection ||
      navigator.webkitConnection || {
        downlink: 1,
        effectiveType: "4g",
      }
    );
  }
}